import { create } from 'zustand';
import type { OutboxItem, SyncStatus } from './types';

export interface SyncToast {
  id: string;
  message: string;
}

interface SyncState {
  status: SyncStatus;
  pendingCount: number;
  /**
   * Retryable failures — будут повторены автоматически по next_retry_at.
   * UI обычно показывает их компактно («синхронизация задерживается, N не отправлено»).
   */
  failed: OutboxItem[];
  /**
   * 1.18.0 — Dead-letter: записи которые не могут синхронизироваться
   * автоматически. Permanent ошибки (RLS, FK, validation) или retryable
   * exhausted. Требуют действия пилота/админа в UI dead-letter.
   *
   * Отделяем от failed чтобы UI мог:
   *   • Failed > 0 → синяя/жёлтая плашка «синхронизируем, N в очереди».
   *   • DeadLetter > 0 → красная плашка «N не отправлено, нужно вмешательство»
   *     с кнопкой «открыть».
   */
  deadLetter: OutboxItem[];
  /**
   * 1.18.3 — Флаг «идёт первая синхронизация после логина».
   *
   * Прогрев customers/objects/invoices в `authStore.applyUserState`
   * происходит fire-and-forget. Если пилот сразу открыл
   * CustomerSearchField до окончания прогрева, кэш пустой —
   * выпадашка не показывает существующих клиентов 1-3 секунды.
   *
   * UI компоненты, чувствительные к этой задержке (CustomerSearchField,
   * ObjectRegistrationStep), смотрят на этот флаг и показывают
   * «Загружаем данные…» вместо «Ничего не найдено».
   *
   * Флаг ставится в true при старте прогрева и сбрасывается когда все
   * 4 sync функции отработали (успех или ошибка — не важно, факт
   * завершения).
   */
  firstSyncInProgress: boolean;
  lastSyncAt: string | null;
  toast: SyncToast | null;
  setStatus: (status: SyncStatus) => void;
  setPendingCount: (count: number) => void;
  setFailed: (failed: OutboxItem[]) => void;
  setDeadLetter: (deadLetter: OutboxItem[]) => void;
  setFirstSyncInProgress: (inProgress: boolean) => void;
  markSynced: () => void;
  showToast: (message: string) => void;
  clearToast: () => void;
}

export const useSyncStore = create<SyncState>()((set) => ({
  status: navigator.onLine ? 'synced' : 'offline',
  pendingCount: 0,
  failed: [],
  deadLetter: [],
  firstSyncInProgress: false,
  lastSyncAt: null,
  toast: null,
  setStatus: (status) => set({ status }),
  setPendingCount: (pendingCount) => set({ pendingCount }),
  setFailed: (failed) => set({ failed }),
  setDeadLetter: (deadLetter) => set({ deadLetter }),
  setFirstSyncInProgress: (firstSyncInProgress) => set({ firstSyncInProgress }),
  markSynced: () => set({ lastSyncAt: new Date().toISOString(), status: 'synced' }),
  showToast: (message) => set({ toast: { id: crypto.randomUUID(), message } }),
  clearToast: () => set({ toast: null }),
}));
